import { injectable, inject } from 'inversify';
import { GTD_DOMAIN_SYMBOLS } from '../../../../infrastructure/di/GTDDomainSymbols';
import { z } from 'zod';
import { Project } from '../../domain/aggregates/Project';
import { ProjectRepository } from '../../infrastructure/repositories/ProjectRepository';
import { BaseApplicationError } from '@swoft/core';

/**
 * Command Schemas with Zod Validation
 */
export const CreateProjectCommandSchema = z.object({
  name: z.string().min(1, 'Project name cannot be empty').max(200, 'Project name too long'),
  desiredOutcome: z.string().min(1, 'Desired outcome is required').max(1000, 'Outcome description too long'),
  area: z.string().optional(),
  reviewDate: z.date().optional(),
  createdBy: z.string().min(1, 'Created by is required')
}).strict();

export const UpdateProjectCommandSchema = z.object({
  projectId: z.string().min(1, 'Project ID is required'),
  name: z.string().min(1).max(200).optional(),
  desiredOutcome: z.string().min(1).max(1000).optional(),
  area: z.string().optional(),
  reviewDate: z.date().optional(),
  status: z.enum(['active', 'on-hold', 'completed', 'cancelled']).optional()
}).strict();

export const CompleteProjectCommandSchema = z.object({
  projectId: z.string().min(1, 'Project ID is required'),
  completedBy: z.string().min(1, 'Completed by is required')
}).strict();

// Command Types
export type CreateProjectCommand = z.infer<typeof CreateProjectCommandSchema>;
export type UpdateProjectCommand = z.infer<typeof UpdateProjectCommandSchema>;
export type CompleteProjectCommand = z.infer<typeof CompleteProjectCommandSchema>;

// Result Types
export interface CreateProjectResult {
  readonly projectId: string;
  readonly success: boolean;
  readonly message: string;
}

export interface ProjectOperationResult {
  readonly success: boolean;
  readonly message: string;
}

// Error classes
export class InvalidProjectCommandError extends BaseApplicationError {
  readonly code = 'INVALID_PROJECT_COMMAND';
  
  constructor(validationErrors: string[]) {
    super(
      'Invalid project command data',
      {
        ux: [
          'Please check the following and try again:',
          ...validationErrors
        ],
        dx: [`Command validation failed: ${validationErrors.join(', ')}`]
      }
    );
  }
}

export class ProjectNotFoundError extends BaseApplicationError {
  readonly code = 'PROJECT_NOT_FOUND';
  
  constructor(projectId: string) {
    super(
      `Project not found: ${projectId}`,
      {
        ux: [
          'The requested project could not be found.',
          'It may have been deleted or the ID is incorrect.'
        ],
        dx: [`Project lookup failed for ID: ${projectId}`]
      }
    );
  }
}

/**
 * ProjectWriteService - Command Side (CQRS)
 * 
 * Handles all write operations for GTD projects.
 * Implements the GTD principle of organizing multi-step outcomes.
 */
@injectable()
export class ProjectWriteService {
  constructor(
    @inject(GTD_DOMAIN_SYMBOLS.ProjectRepository)
    private readonly repository: ProjectRepository
  ) {}

  /**
   * Create a new GTD project
   * Core GTD principle: Any outcome requiring more than one action
   */
  async createProject(command: CreateProjectCommand): Promise<CreateProjectResult> {
    // 1. Command validation
    let validatedCommand: CreateProjectCommand;
    try {
      validatedCommand = CreateProjectCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidProjectCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    try {
      // 2. Create domain aggregate
      const project = Project.create(
        validatedCommand.name,
        validatedCommand.desiredOutcome,
        validatedCommand.createdBy,
        validatedCommand.area,
        validatedCommand.reviewDate
      );

      // 3. Persist
      await this.repository.save(project);

      return {
        projectId: project.id,
        success: true,
        message: 'Project created successfully'
      };

    } catch (error) {
      return {
        projectId: '',
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create project'
      };
    }
  }

  /**
   * Update project details
   * GTD: Projects can evolve as understanding improves
   */
  async updateProject(command: UpdateProjectCommand): Promise<ProjectOperationResult> {
    // 1. Command validation
    let validatedCommand: UpdateProjectCommand;
    try {
      validatedCommand = UpdateProjectCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidProjectCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    // 2. Load project
    const project = await this.repository.findById(validatedCommand.projectId);
    if (!project) {
      throw new ProjectNotFoundError(validatedCommand.projectId);
    }

    try {
      // 3. Apply updates
      // Note: The Project aggregate would need update methods for this to work properly
      // For now, we'll return not implemented
      throw new Error('Project update not yet implemented in domain aggregate');

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update project'
      };
    }
  }

  /**
   * Complete a project
   * Core GTD principle: Mark as done when outcome achieved
   */
  async completeProject(command: CompleteProjectCommand): Promise<ProjectOperationResult> {
    // 1. Command validation
    let validatedCommand: CompleteProjectCommand;
    try {
      validatedCommand = CompleteProjectCommandSchema.parse(command);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new InvalidProjectCommandError(error.errors.map(e => e.message));
      }
      throw error;
    }

    // 2. Load project
    const project = await this.repository.findById(validatedCommand.projectId);
    if (!project) {
      throw new ProjectNotFoundError(validatedCommand.projectId);
    }

    try {
      // 3. Complete project
      project.complete(validatedCommand.completedBy);

      // 4. Persist
      await this.repository.save(project);

      return {
        success: true,
        message: 'Project completed successfully'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to complete project'
      };
    }
  }

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<ProjectOperationResult> {
    try {
      const project = await this.repository.findById(projectId);
      if (!project) {
        throw new ProjectNotFoundError(projectId);
      }

      await this.repository.deleteById(projectId);

      return {
        success: true,
        message: 'Project deleted successfully'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to delete project'
      };
    }
  }

  /**
   * Put project on hold
   * GTD: Temporarily suspend active work
   */
  async holdProject(projectId: string): Promise<ProjectOperationResult> {
    try {
      const project = await this.repository.findById(projectId);
      if (!project) {
        throw new ProjectNotFoundError(projectId);
      }

      project.defer();
      await this.repository.save(project);

      return {
        success: true,
        message: 'Project put on hold'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to put project on hold'
      };
    }
  }

  /**
   * Reactivate a project
   * GTD: Resume work on held project
   */
  async activateProject(projectId: string): Promise<ProjectOperationResult> {
    try {
      const project = await this.repository.findById(projectId);
      if (!project) {
        throw new ProjectNotFoundError(projectId);
      }

      project.activate();
      await this.repository.save(project);

      return {
        success: true,
        message: 'Project reactivated'
      };

    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to activate project'
      };
    }
  }
}